import { MutableRefObject, useCallback } from "react";
import { VideoDimension } from "components/stories/types";
import { getSubdomain } from "app/utils";
import { makePatch, makePost } from "@culturehq/client";
import { ElementState } from "@creatomate/preview";
import { isMediaVideoType } from "../utils";
import { PostStoryProviderProps, PostStoryState, SelectedMedia, SetState } from "../types";

type UsePostStory = {
  state: Partial<PostStoryState>;
  setState: SetState;
  videoDimensionRef: MutableRefObject<VideoDimension | undefined>;
  prompts: PostStoryProviderProps["prompts"];
  onAnotherModalOpen: PostStoryProviderProps["onAnotherModalOpen"];
  onNotification: PostStoryProviderProps["onNotification"];
  onPromptChange: PostStoryProviderProps["onPromptChange"];
  onPosted: PostStoryProviderProps["onPosted"];
  onClose: () => void;
  closeModal: () => void;
  restartState: () => void;
};

type ExtendedElementState = ElementState & {
  type?: string;
}

type MappedMediaData = {[key: number]: SelectedMedia}

const usePostStory = ({
  state: { selectedMedia, selectedPrompt, webVideoRecorded, currentStory },
  setState,
  videoDimensionRef,
  prompts,
  onAnotherModalOpen,
  onNotification,
  onPosted = () => {},
  onClose,
  closeModal,
  restartState
}: UsePostStory) => {
  const postStory = useCallback(
    async (
      question,
      body,
      slack_channel,
      visuals,
      mediaType,
      videoLength,
      mediaData,
      newPublicUser,
      questions,
      suggestions,
      mappersValueIds
    ) => {
      let response;
      let params: any = {
        question,
        body,
        visuals,
        mediaType,
        duration: videoLength,
        height: videoDimensionRef.current?.height,
        width: videoDimensionRef.current?.width,
        processVideo: isMediaVideoType(selectedMedia as Blob),
        web: true,
        recordedVideo: webVideoRecorded,
        mediaData: undefined,
        externalState: undefined,
        questions,
        suggestions,
        mappersValueIds
      };

      if (mediaData.length > 0) {
        const mediaDataObj: MappedMediaData = {};
        mediaData.forEach((data: SelectedMedia | any, index: number) => {
          if (data !== null) {
            mediaDataObj[index] = {
              ...data,
              elements: data?.elements?.map((element: ExtendedElementState) => {
                if (
                  element.source?.startsWith(
                    `https://${getSubdomain()}.culturehq.com/`
                  )
                ) {
                  const properties = {
                    ...element,
                    source: visuals[index].mediaUrl
                  };
                  return element.type === "video"
                    ? {
                      ...properties,
                      name: `media-${visuals[index].mediaUrl}`
                    }
                    : properties;
                }
                return element;
              })
            };
          }
        });

        params = { ...params, mediaData: mediaDataObj };
      }

      if (currentStory) {
        params.externalState = currentStory.externalState as string;
        response = await makePatch(`/stories/${currentStory.id}`, params);
      } else {
        params.slackChannel = slack_channel;
        response = newPublicUser
          ? await makePost("/stories/public", {
            ...params,
            creator: newPublicUser,
            id: selectedPrompt?.id
          }).then((res: any) => {
            if (onNotification) {
              onNotification({
                id: new Date().getTime(),
                notificationType: "public_video_story_created",
                entity: res.story
              });
            }
            return res;
          })
          : await makePost("/stories", params);
      }
      setState({ progress: 100 });
      if (onAnotherModalOpen) onAnotherModalOpen(false);
      restartState();
      closeModal();
      onClose();
      if (response.story) onPosted(response.story, response.userActivity);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedMedia,
      webVideoRecorded,
      currentStory,
      onAnotherModalOpen,
      restartState,
      prompts,
      selectedPrompt,
      closeModal,
      onClose,
      onPosted
    ]
  );

  const onAWSUploadedFile = useCallback(
    async (
      question,
      body,
      channel,
      visuals,
      mediaType,
      videoLength,
      mediaData,
      newPublicUser,
      questions,
      suggestions,
      mappersValueIds
    ) => {
      await postStory(
        question,
        body,
        channel,
        visuals,
        mediaType,
        videoLength,
        mediaData,
        newPublicUser,
        questions,
        suggestions,
        mappersValueIds
      );
    },
    [postStory]
  );

  return useCallback(
    async (
      ms,
      question,
      body,
      channel,
      visuals,
      mediaType,
      videoLength,
      mediaData,
      newPublicUser,
      questions,
      suggestions,
      mappersValueIds
    ) => new Promise(_ => {
      // This timeout is to allow AWS to upload the file, in this point,
      // AWs has responded with a 200 status, but we need to take care of
      // S3 Consistency Model
      setTimeout(
        onAWSUploadedFile,
        ms,
        question,
        body,
        channel,
        visuals,
        mediaType,
        videoLength,
        mediaData,
        newPublicUser,
        questions,
        suggestions,
        mappersValueIds
      );
    }),
    [onAWSUploadedFile]
  );
};

export default usePostStory;
